import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import { REVIEW_TIERS } from "@/lib/reviewPricing";

/**
 * ReviewersPage: `/reviewers`, the public recruiting page for reviewers.
 *
 * Positioning (CEO decision, 2026-10-12): lead with the code wedge. Xpert
 * reviews AI-built apps and code before they ship; every verdict is signed by
 * a named reviewer. Product and strategy reviews are
 * "also available", never the lead.
 *
 * Honesty rules for this page (go-live plan 3.3): no invented counts,
 * testimonials, logos, "join N experts" or earnings claims; no employer
 * names; no promised volume. Every number below is either one of the three
 * list prices (lib/reviewPricing), the 80% reviewer share derived from them,
 * or a fact the CEO Office approved for recruiting copy.
 *
 * Single conversion target: /become-expert?role=reviewer, which preselects
 * the review options on the existing application form.
 *
 * Sections: hero, how it works (4 steps), what you earn, what we expect,
 * ground rules, where we are, FAQ, closing CTA.
 */

export const APPLY_HREF = "/become-expert?role=reviewer";

// The reviewer keeps 80% of the list price. Money is computed in cents so the
// displayed dollar amounts never carry float noise ($99 -> $79.20).
const REVIEWER_SHARE_PERCENT = 80;
export function reviewerShare(price) {
  const cents = Math.round(price * 100 * REVIEWER_SHARE_PERCENT / 100);
  return `$${(cents / 100).toFixed(2)}`;
}

export const PAGE_TITLE = "Become a reviewer | Xpert";
export const PAGE_DESCRIPTION =
  "Xpert reviews AI-built apps and code before they ship. Reviewers are paid " +
  "per review to check the work and sign a written verdict. You keep 80% of every review.";

const HOW_STEPS = [
  {
    n: "1",
    title: "Apply",
    body: "Tell us who you are and link your public work: GitHub, writing, talks or products you shipped.",
  },
  {
    n: "2",
    title: "10-minute check",
    body: "We check your identity, your claims against your public work, and your profile.",
  },
  {
    n: "3",
    title: "Paid calibration review",
    body: "One paid review ($50), written the way you would write any other. You have 48 hours, and it is scored against a rubric. You are paid the $50 whether or not you pass.",
  },
  {
    n: "4",
    title: "You are listed",
    body: "Pass and you are listed as a reviewer. Reviews are paid through Stripe Connect after delivery.",
  },
];

const MUST_HAVES = [
  ["8+ years building software", "At least eight years of building software."],
  ["Public evidence of your work", "GitHub, published writing, talks or products we can open and check."],
  ["Clear written English", "The deliverable is a written verdict, so how you write is the job."],
  ["Capacity", "Room to take reviews when they come: freelance, fractional, indie or between roles."],
];

const GROUND_RULES = [
  ["Confidential, under an NDA", "What buyers send you is covered by an NDA."],
  ["AI tools are allowed, the signature is yours", "Use whatever tools make you faster. You read the work, you form the judgement, and you sign the verdict."],
  ["You delete your copies", "Delete your copies of buyer material within 90 days of delivering the verdict. How long Xpert itself keeps what buyers submit is described in our privacy policy."],
];

const FAQ_ITEMS = [
  [
    "Why not just use AI?",
    "AI can read code, and you are welcome to use it while you review. What a buyer pays for is accountability: a named reviewer signs the verdict and puts their name behind it. A model cannot do that. You can.",
  ],
  [
    "How many reviews will I get?",
    "We do not know yet and we will not promise a number. It is early and you would be reviewer two to five. We will share the real numbers with you monthly.",
  ],
  [
    "How do I get paid?",
    "You keep 80% of the review price. Payment goes through Stripe Connect after you deliver the verdict.",
  ],
  [
    "What is the calibration review?",
    "One paid review that shows us how you review. You write the same kind of verdict a buyer would receive, within 48 hours, and it is scored against a rubric. You are paid the $50 whether or not you pass. Pass and you are listed.",
  ],
  [
    "What do I review, and how long does it take?",
    "AI-built apps and code before they ship. Launch categories are code review, shipping an app and engineering, with product and strategy reviews also available. Plan on one to two hours per review.",
  ],
];

// ---------------------------------------------------------------------------
// Page meta. The repo has no helmet-style library and public/index.html only
// carries the site-wide title, so this page sets its own title and meta
// description while mounted and restores the previous values on unmount.
// ---------------------------------------------------------------------------

function usePageMeta(title, description) {
  useEffect(() => {
    const prevTitle = document.title;
    let meta = document.querySelector('meta[name="description"]');
    const created = !meta;
    const prevDescription = meta ? meta.getAttribute("content") : null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    document.title = title;
    meta.setAttribute("content", description);
    return () => {
      document.title = prevTitle;
      if (created) meta.remove();
      else meta.setAttribute("content", prevDescription ?? "");
    };
  }, [title, description]);
}

export default function ReviewersPage() {
  usePageMeta(PAGE_TITLE, PAGE_DESCRIPTION);
  return (
    <div className="min-h-screen bg-canvas text-ink font-body">
      <MarketingNav />
      <Hero />
      <HowItWorks />
      <Earnings />
      <Expectations />
      <GroundRules />
      <WhereWeAre />
      <FAQ />
      <FinalCTA />
      <MarketingFooter />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------

function ApplyButton({ children = "Apply to review" }) {
  return (
    <Link
      to={APPLY_HREF}
      className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-brand-green hover:bg-brand-greenHover
                 text-white font-semibold text-sm tracking-wide shadow-sm transition-colors"
    >
      {children} <ArrowRight className="w-4 h-4" />
    </Link>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div aria-hidden="true"
           className="absolute -top-32 -right-24 w-[640px] h-[640px] rounded-full bg-brand-green/10 blur-3xl pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-16 sm:pb-24">
        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-16 items-start">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-green">
              The human review layer for AI-built work
            </p>
            <h1 className="font-display text-5xl sm:text-6xl text-ink leading-[1.05] mt-5">
              Review AI-built code.{" "}
              <em className="not-italic">
                <span className="italic text-brand-green">Sign the verdict.</span>
              </em>
            </h1>
            <p className="mt-6 text-lg text-slateline-600 leading-relaxed max-w-xl">
              Xpert reviews AI-built apps and code before they ship. Every
              verdict is signed by a named reviewer.{" "}
              <strong className="text-ink font-semibold">
                We are looking for reviewers to join the founder, paid per review.
              </strong>
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <ApplyButton />
              <a href="#how"
                 className="inline-flex items-center h-12 px-2 text-sm font-medium text-ink hover:text-brand-green transition-colors">
                See how it works ↓
              </a>
            </div>

            <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-[13px] text-slateline-600">
              {[
                "You keep 80% of every review",
                "One to two hours per review",
                "No volume promised, and we say so up front",
              ].map((t) => (
                <span key={t} className="inline-flex items-center gap-2">
                  <Check className="w-3.5 h-3.5 text-brand-green" />
                  {t}
                </span>
              ))}
            </div>
          </div>

          {/* The role at a glance. A description, not a sample review. */}
          <aside className="bg-white border border-slateline-200 rounded-xl p-6 shadow-sm flex flex-col gap-3.5">
            <div className="flex items-center gap-2 pb-3 border-b border-slateline-100">
              <span className="w-2 h-2 rounded-full bg-brand-green" />
              <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slateline-500">
                The role at a glance
              </span>
            </div>
            <GlanceRow k="you review" v="AI-built apps and code, before they ship" green />
            <GlanceRow k="categories" v="Code review · shipping an app · engineering" />
            <GlanceRow k="also" v="Product and strategy reviews" />
            <GlanceRow k="you deliver" v="A written verdict that you sign" />
            <GlanceRow k="turnaround" v="24 hours · 48 hours · 1 week" />
            <GlanceRow k="time" v="One to two hours per review" />
            <div className="mt-1 p-3 bg-brand-greenPale border border-brand-green/30 rounded-md flex items-center gap-2.5 text-[13px] leading-snug">
              <Check className="w-4 h-4 text-brand-green flex-shrink-0" />
              <div>
                <strong className="text-ink font-semibold">A named reviewer signs.</strong>{" "}
                <span className="text-slateline-600">That is what the buyer is paying for.</span>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

function GlanceRow({ k, v, green = false }) {
  return (
    <div className="grid grid-cols-[88px_1fr] gap-2.5 items-start text-[13px] leading-snug">
      <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-slateline-500 pt-0.5">{k}</span>
      <span className={green ? "text-ink font-semibold" : "text-slateline-600"}>{v}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// How it works
// ---------------------------------------------------------------------------

function HowItWorks() {
  return (
    <section id="how" className="py-20 sm:py-28 scroll-mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead
          eyebrow="How it works"
          h2={<>Four steps.<br />Paid from step three.</>}
          sub="After a short check you do one paid review. Pass it and you are listed."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 mt-12">
          {HOW_STEPS.map((s) => (
            <article key={s.n} className="bg-white border border-slateline-200 rounded-xl p-6">
              <div className="w-10 h-10 rounded-md bg-brand-greenPale text-brand-green
                              flex items-center justify-center font-display text-lg font-bold">
                {s.n}
              </div>
              <h3 className="mt-5 font-display text-xl text-ink leading-tight">{s.title}</h3>
              <p className="mt-3 text-[15px] text-slateline-600 leading-relaxed">{s.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// What you earn
// ---------------------------------------------------------------------------

function Earnings() {
  return (
    <section id="earn" className="py-20 sm:py-28 scroll-mt-16 bg-white border-y border-slateline-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead
          eyebrow="What you earn"
          h2={<>You keep 80%<br />of every review.</>}
          sub="Buyers pay one list price per turnaround. Your share is paid through Stripe Connect after you deliver."
        />
        <div className="grid sm:grid-cols-3 gap-5 mt-12 max-w-4xl mx-auto">
          {REVIEW_TIERS.map((t) => (
            <article key={t.key}
                     className="bg-canvas border border-slateline-200 rounded-xl p-6 sm:p-7 flex flex-col">
              <div className="text-sm font-semibold text-brand-green">{t.label} turnaround</div>
              <div className="mt-4 text-sm text-slateline-500">Buyer pays ${t.price}</div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="font-display text-5xl font-semibold text-ink leading-none">
                  {reviewerShare(t.price)}
                </span>
                <span className="text-sm text-slateline-500">to you</span>
              </div>
            </article>
          ))}
        </div>
        <p className="mt-8 text-center text-sm text-slateline-600 max-w-xl mx-auto leading-relaxed">
          A review takes one to two hours. This is not a salary, and no volume is
          promised: you are paid for each review you deliver.
        </p>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// What we expect
// ---------------------------------------------------------------------------

function Expectations() {
  return (
    <section id="expect" className="py-20 sm:py-28 scroll-mt-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead
          eyebrow="What we expect"
          h2={<>Four must-haves.</>}
          sub="This is what we look for before you are listed."
        />
        <div className="grid sm:grid-cols-2 gap-5 mt-12">
          {MUST_HAVES.map(([title, body]) => (
            <article key={title} className="bg-white border border-slateline-200 rounded-xl p-6 flex gap-4">
              <span className="mt-0.5 w-6 h-6 rounded-full bg-brand-greenPale text-brand-green flex items-center justify-center flex-shrink-0">
                <Check className="w-3.5 h-3.5" />
              </span>
              <div>
                <h3 className="font-display text-lg text-ink leading-tight">{title}</h3>
                <p className="mt-2 text-[15px] text-slateline-600 leading-relaxed">{body}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Ground rules
// ---------------------------------------------------------------------------

function GroundRules() {
  return (
    <section id="rules" className="py-20 sm:py-28 scroll-mt-16 bg-white border-y border-slateline-200">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead
          eyebrow="Ground rules"
          h2={<>Three rules we hold<br />ourselves to.</>}
        />
        <div className="grid md:grid-cols-3 gap-5 mt-12">
          {GROUND_RULES.map(([title, body]) => (
            <article key={title} className="bg-canvas border border-slateline-200 rounded-xl p-6">
              <h3 className="font-display text-lg text-ink leading-tight">{title}</h3>
              <p className="mt-3 text-[15px] text-slateline-600 leading-relaxed">{body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Where we are
// ---------------------------------------------------------------------------

function WhereWeAre() {
  return (
    <section id="where" className="py-20 sm:py-28 scroll-mt-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead
          eyebrow="Where we are"
          h2={<>Early, and we say so.</>}
        />
        <div className="mt-10 bg-white border border-slateline-200 rounded-xl p-6 sm:p-8 text-base text-slateline-600 leading-relaxed space-y-4">
          <p>
            Today the only reviewer is the founder. He is Ankit Kumar, with 15
            years in data, lending and product.
          </p>
          <p>
            You would be reviewer two to five. We promise no volume. We will
            share the real numbers with you every month, whatever they are.
          </p>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// FAQ
// ---------------------------------------------------------------------------

function FAQ() {
  return (
    <section id="faq" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 sm:pb-28 scroll-mt-16">
      <SectionHead eyebrow="Common questions" h2={<>Things reviewers ask first.</>} />
      <div className="mt-10 bg-white border border-slateline-200 rounded-xl overflow-hidden">
        {FAQ_ITEMS.map(([q, a]) => (
          <details key={q} className="group border-b border-slateline-200 last:border-b-0">
            <summary className="cursor-pointer list-none flex items-center justify-between gap-4 px-6 sm:px-7 py-5 font-display text-[17px] text-ink">
              <span>{q}</span>
              <span className="text-slateline-400 text-2xl font-light leading-none transition-transform duration-200 group-open:rotate-45 select-none">
                +
              </span>
            </summary>
            <div className="px-6 sm:px-7 pb-5 text-sm leading-relaxed text-slateline-600">{a}</div>
          </details>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Closing CTA
// ---------------------------------------------------------------------------

function FinalCTA() {
  return (
    <section id="cta" className="relative bg-ink text-white overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[480px] rounded-full
                   bg-brand-green/30 blur-3xl pointer-events-none"
      />
      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 text-center">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-green">
          Reviewers wanted
        </p>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl leading-tight">
          Review the code.<br />
          <em className="italic text-brand-green">Sign your name to it.</em>
        </h2>
        <p className="mt-6 text-base sm:text-lg text-white/70 leading-relaxed">
          Apply, pass one paid calibration review, and get listed.
        </p>
        <div className="mt-8 flex justify-center">
          <ApplyButton />
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Shared: section head (eyebrow + h2 + optional sub)
// ---------------------------------------------------------------------------

function SectionHead({ eyebrow, h2, sub }) {
  return (
    <div className="text-center max-w-2xl mx-auto">
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-green">{eyebrow}</p>
      <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-ink">
        {h2}
      </h2>
      {sub && (
        <p className="mt-5 text-base sm:text-lg text-slateline-600 leading-relaxed">{sub}</p>
      )}
    </div>
  );
}
